import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiX, FiCheckCircle, FiCpu, FiAlertTriangle } from 'react-icons/fi';

export default function ChallengeModal({ isOpen, onClose }) {
  const [step, setStep] = useState('loading'); // loading, question, success

  useEffect(() => {
    if (isOpen) {
      setStep('loading');
      const timer = setTimeout(() => setStep('question'), 1500);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const handleAnswer = () => {
    setStep('success');
    setTimeout(() => {
      onClose();
    }, 2500);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy-950/80 backdrop-blur-sm">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-navy-900 border border-navy-700 rounded-2xl w-full max-w-lg overflow-hidden relative shadow-2xl shadow-quest-500/10"
        >
          {/* Header */}
          <div className="flex justify-between items-center p-4 border-b border-navy-800 bg-navy-800/50">
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiCpu} className="text-neon-blue" />
              <span className="font-mono text-sm uppercase tracking-wider text-gray-300">Daily Boss Encounter</span>
            </div>
            {step !== 'loading' && step !== 'success' && (
              <button onClick={onClose} className="text-gray-400 hover:text-white">
                <SafeIcon icon={FiX} />
              </button>
            )}
          </div>

          <div className="p-8 min-h-[300px] flex flex-col justify-center relative">
            {step === 'loading' && (
              <motion.div 
                initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="flex flex-col items-center justify-center text-center gap-4"
              >
                <div className="w-16 h-16 border-4 border-navy-700 border-t-quest-500 rounded-full animate-spin shadow-glow-green" />
                <p className="font-mono text-quest-500 animate-pulse">Establishing Connection...</p>
              </motion.div>
            )}

            {step === 'question' && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                <div className="mb-6">
                  <span className="inline-block px-2 py-1 rounded bg-navy-800 text-neon-pink text-xs font-bold mb-3 border border-neon-pink/30">HOCs vs Hooks</span>
                  <h3 className="text-xl font-medium leading-relaxed">
                    Which scenario optimally favors using a Custom Hook over a Higher-Order Component (HOC)?
                  </h3>
                </div>
                <div className="space-y-3">
                  {['When you need to share stateful logic without changing component hierarchy.',
                    'When you need to wrap a component with multiple layers of UI wrappers.',
                    'When dealing with class components exclusively.',
                    'When you want to inject props directly into the DOM node.'].map((ans, i) => (
                    <button
                      key={i}
                      onClick={i === 0 ? handleAnswer : null}
                      className="w-full text-left p-4 rounded-xl border border-navy-700 bg-navy-800/30 hover:bg-navy-800 hover:border-quest-500/50 transition-all group"
                    >
                      <span className="text-gray-400 font-mono mr-3 group-hover:text-quest-500">0{i+1}</span>
                      <span className="text-gray-200">{ans}</span>
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {step === 'success' && (
              <motion.div 
                initial={{ scale: 0.8, opacity: 0 }} 
                animate={{ scale: 1, opacity: 1 }}
                className="flex flex-col items-center justify-center text-center gap-6"
              >
                <div className="w-24 h-24 rounded-full bg-quest-500/20 flex items-center justify-center border-2 border-quest-500 shadow-glow-green">
                  <SafeIcon icon={FiCheckCircle} className="text-5xl text-quest-500" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2">Encounter Cleared!</h2>
                  <p className="text-gray-400">Logic successfully compiled.</p>
                </div>
                <div className="flex gap-4">
                  <div className="bg-navy-800 px-4 py-2 rounded-lg border border-navy-700">
                    <span className="text-trophy-500 font-bold block text-xl">+50</span>
                    <span className="text-xs text-gray-500 uppercase tracking-wider text-center block">XP</span>
                  </div>
                  <div className="bg-navy-800 px-4 py-2 rounded-lg border border-navy-700">
                    <span className="text-quest-500 font-bold block text-xl">+1</span>
                    <span className="text-xs text-gray-500 uppercase tracking-wider text-center block">Streak</span>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}